import type { OrderReportResponse } from '@/types/OrderReportTypes'
import { InfoCircle } from 'flowbite-react-icons/outline'

import { formatReportTimestamp } from './reportTimestamp'

/**
 * Warning banner shown when the latest report is critical and not yet
 * acknowledged. Shared by the results page and the guidance page so a provider
 * sees (and can acknowledge) an unreviewed critical result from either view.
 */
export function CriticalResultBanner({
  reports,
  onAcknowledge,
  isConfirming,
}: {
  reports: OrderReportResponse[]
  onAcknowledge: () => void
  isConfirming: boolean
}) {
  return (
    <div
      className="flex w-full flex-col gap-4 rounded-base border border-[var(--colors-border-border-warning-subtle,#FDBA8C)] bg-[var(--colors-background-bg-warning-soft,#FFF8F1)] p-4"
      role="alert"
    >
      <div className="flex flex-col gap-2">
        <div className="flex items-center gap-2">
          <InfoCircle className="h-4 w-4 shrink-0 text-[var(--colors-text-text-fg-warning,#B43403)]" />
          <p className="text-lg font-semibold leading-5 tracking-tight text-[var(--colors-text-text-fg-warning,#B43403)]">
            Critical Result
          </p>
        </div>
        <div className="font-sans text-sm font-normal leading-5 text-[var(--colors-text-text-fg-warning,#B43403)]">
          {reports.length === 0 ? (
            <p>Final report was generated. Review and acknowledge within 24 hours.</p>
          ) : (
            reports.map((report) => {
              const ts = formatReportTimestamp(report.generated_at) ?? formatReportTimestamp(report.created_at)
              return (
                <p key={report.id}>
                  Final report was generated{ts ? ` on ${ts}` : ''}. Review and acknowledge within 24 hours.
                </p>
              )
            })
          )}
        </div>
      </div>
      <button
        type="button"
        onClick={onAcknowledge}
        disabled={isConfirming}
        className="inline-flex items-center justify-center gap-1.5 self-start whitespace-nowrap rounded-full bg-[var(--colors-background-bg-warning,#FF5A1F)] px-2.5 py-1.5 text-xs font-medium text-white shadow-xs transition-colors hover:opacity-90 disabled:opacity-60"
      >
        {isConfirming ? 'Confirming…' : 'Acknowledge'}
      </button>
    </div>
  )
}

export default CriticalResultBanner
